import { Link } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import useArticleListQuery from '../../hooks/article';
import { maxSymbolEllipsis } from '../../helpers/functions';

const NewsGrid = () => {
    const { articles, isLoading } = useArticleListQuery();

    return (
        <div className="my-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-slate-800 text-lg font-bold tracking-tight">
                    Статьи и новости
                </h3>
                {articles?.length > 0 && (
                    <Link
                        to="/articles"
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline transition"
                    >
                        Смотреть все →
                    </Link>
                )}
            </div>

            <div className="grid gap-6 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-4 sm:grid-cols-3 xs:grid-cols-2">
                {isLoading
                    ? Array.from(new Array(4)).map((item, index) => (
                          <NewsItemSkeleton key={index} />
                      ))
                    : articles?.map((item, index) => (
                          <NewsItem key={index} item={item} />
                      ))}
            </div>
        </div>
    );
};

function NewsItem({ item }) {
    return (
        <div className="group flex flex-col justify-between rounded-2xl border border-slate-100 bg-white hover:border-blue-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer">
            <Link to={`/article/${item.id}`} className="flex flex-col h-full">
                {/* Image Section */}
                <div className="w-full h-[170px] overflow-hidden bg-slate-50 relative">
                    <img
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        src={item?.image}
                        alt={item.title || 'News image'}
                    />
                </div>

                {/* Content Section */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                        <h4 className="text-slate-800 text-[14px] font-semibold line-clamp-1 group-hover:text-blue-600 transition-colors mb-1.5">
                            {item.title}
                        </h4>
                        <p className="text-slate-500 text-xs leading-relaxed line-clamp-2 mb-4">
                            {maxSymbolEllipsis(item.text, 70)}
                        </p>
                    </div>

                    <div className="pt-3 border-t border-slate-50 space-y-2.5">
                        {/* Region and Date */}
                        <div className="flex justify-between items-center text-[11px] text-slate-400">
                            <span className="flex items-center">
                                <svg className="w-3.5 h-3.5 mr-1 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                г.Бишкек
                            </span>
                            <span>Сегодня 13:01</span>
                        </div>

                        {/* Reactions with elegant SVGs */}
                        <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                <svg className="w-4 h-4 text-slate-300 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <span className="font-semibold text-slate-600">10</span>
                            </div>

                            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                                <svg className="w-4 h-4 text-slate-300 group-hover:text-rose-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span className="font-semibold text-slate-600">50</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
}

function NewsItemSkeleton() {
    return (
        <div className="w-full rounded-2xl border border-slate-100 shadow-sm flex flex-col overflow-hidden bg-white">
            <div className="w-full h-[170px]">
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </div>

            <div className="p-4 space-y-3">
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="100%" height={14} />

                <div className="pt-3 border-t border-slate-50 flex justify-between items-center">
                    <Skeleton variant="text" width="30%" />
                    <Skeleton variant="text" width="20%" />
                </div>
            </div>
        </div>
    );
}

export default NewsGrid;